import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, MailCheck, Zap } from "lucide-react";
import { LOGO_URL } from "../config";
import { useLang, pick } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

export default function PaymentSuccess() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [status, setStatus] = useState("claim");
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", course_id: params.get("course") || "" });
  const [sending, setSending] = useState(false);
  const [claimError, setClaimError] = useState("");

  useEffect(() => {
    axios.get(`${API}/courses`).then((res) => setCourses(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  const submitClaim = async (e) => {
    e.preventDefault();
    setSending(true);
    setClaimError("");
    try {
      if (sessionId) {
        const res = await axios.post(`${API}/access/auto-claim`, { ...form, session_id: sessionId, origin_url: window.location.origin });
        setStatus(res.data.status === "already" ? "already" : "claimed");
      } else {
        const res = await axios.post(`${API}/access/request`, { ...form, origin_url: window.location.origin });
        setStatus(res.data.already ? "already" : "claimed");
      }
    } catch (err) {
      setClaimError(err.response?.data?.detail || (ar ? "حدث خطأ" : "Something went wrong"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-black flex items-center justify-center px-4 py-16" data-testid="payment-success-page">
      <div className="text-center border border-luxury-border bg-luxury-surface p-8 md:p-12 max-w-md w-full">
        <img src={LOGO_URL} alt="TheMaya" className="h-10 mx-auto mb-6" />

        {status === "claim" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-3xl mb-3" data-testid="claim-title">{ar ? "خطوة أخيرة" : "One last step"}</p>
            <p className="text-luxury-gray text-sm mb-8">
              {sessionId
                ? (ar
                    ? "أدخل/أدخلي اسمك وبريدك الإلكتروني لتأكيد اشتراكك — سيصلك إيميل مع رابط الدخول المباشر للكورس بعد مراجعة الدفع"
                    : "Enter your name and email to confirm your purchase — your direct course access link arrives by email after payment review")
                : (ar
                    ? "أدخل/أدخلي اسمك وبريدك الإلكتروني لتأكيد اشتراكك — سيصلك إيميل شكر مع رابط الدخول المباشر للكورس بعد مراجعة الدفع"
                    : "Enter your name and email to confirm your purchase — you'll receive a thank-you email with your direct course access link after payment review")}
            </p>
            <form onSubmit={submitClaim} className="space-y-4 text-start" data-testid="claim-form">
              <div>
                <label className="block text-sm text-luxury-gray mb-2">{ar ? "الاسم" : "Name"}</label>
                <input data-testid="claim-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
              </div>
              <div>
                <label className="block text-sm text-luxury-gray mb-2">{ar ? "البريد الإلكتروني (نفس بريد الدفع)" : "Email (same as your payment email)"}</label>
                <input data-testid="claim-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
              </div>
              <div>
                <label className="block text-sm text-luxury-gray mb-2">{ar ? "الدورة" : "Course"}</label>
                <select data-testid="claim-course-select" value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} className={inputCls} required>
                  <option value="">{ar ? "— اختار/اختاري الدورة —" : "— Select the course —"}</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>{pick(lang, c, "title")}</option>
                  ))}
                </select>
              </div>
              {claimError && <p data-testid="claim-error" className="text-red-400 text-sm">{claimError}</p>}
              <button data-testid="claim-submit-button" type="submit" disabled={sending}
                className="w-full bg-luxury-gold text-luxury-black font-semibold py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {ar ? "تأكيد الاشتراك" : "Confirm my purchase"}
              </button>
            </form>
          </>
        )}

        {status === "autodone" && (
          <>
            <Zap className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-3xl mb-3" data-testid="autodone-message">{ar ? "تم فتح وصولك" : "Your access is open"}</p>
            <p className="text-luxury-gray text-sm mb-8">
              {ar
                ? "شكراً لك! وصلك للتو إيميل فيه رابط الدخول المباشر إلى الكورس — بدون كلمة مرور. تفقّد/تفقّدي بريدك الوارد (والبريد غير الهام)"
                : "Thank you! An email with your direct course access link was just sent — no password needed. Check your inbox (and spam folder)"}
            </p>
            <Link to="/" data-testid="autodone-go-home" className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors inline-block">
              {ar ? "العودة للرئيسية" : "Back home"}
            </Link>
          </>
        )}

        {status === "claimed" && (
          <>
            <MailCheck className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-3xl mb-3" data-testid="claim-received-message">{ar ? "تم استلام طلبك" : "Request received"}</p>
            <p className="text-luxury-gray text-sm mb-8">
              {ar
                ? "شكراً لك! بعد تأكيد الدفع سيصلك إيميل شكر يحتوي رابط الدخول المباشر إلى الكورس — بدون كلمة مرور. تفقّد/تفقّدي بريدك الوارد (والبريد غير الهام)"
                : "Thank you! Once your payment is confirmed, you'll receive a thank-you email with your direct course access link — no password needed. Check your inbox (and spam folder)"}
            </p>
            <Link to="/" data-testid="claim-go-home" className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors inline-block">
              {ar ? "العودة للرئيسية" : "Back home"}
            </Link>
          </>
        )}

        {status === "already" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-luxury-gold" strokeWidth={1.25} />
            <p className="text-luxury-bone font-serif text-3xl mb-3" data-testid="claim-already-message">{ar ? "لديك وصول مفعّل لهذه الدورة" : "You already have access to this course"}</p>
            <p className="text-luxury-gray text-sm mb-8">{ar ? "سجّل/سجّلي الدخول إلى حسابك لمشاهدتها — أو اطلب/اطلبي رابط دخول على بريدك" : "Sign in to your account to watch it — or request an email login link"}</p>
            <Link to="/account" data-testid="claim-go-account" className="bg-luxury-gold text-luxury-black font-semibold px-8 py-3 rounded-full hover:bg-luxury-goldLight transition-colors inline-block">
              {ar ? "حسابي" : "My account"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
